import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import { Loader2, Eye, LogOut, MousePointerClick, Clock, ArrowDown, Users, BarChart3, Zap } from "lucide-react";

export default function AdminBehaviorTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/behavior/summary`);
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!data) return <p className="text-gray-500 text-center py-10">Žiadne dáta</p>;

  const funnelPages = ["/", "/login", "/#pricing", "/dashboard", "/results"];
  const funnelData = funnelPages.map(p => {
    const found = data.funnel?.find(f => f.page === p);
    return { page: p, users: found?.unique_users || 0 };
  }).filter(f => f.users > 0);
  const maxFunnel = Math.max(...funnelData.map(f => f.users), 1);

  return (
    <div className="space-y-8" data-testid="admin-behavior-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Celkom eventov" value={data.total_events} color="blue" />
        <StatCard icon={Users} label="Unikátne sessions" value={data.total_sessions} color="emerald" />
        <StatCard icon={Eye} label="Top stránka" value={data.top_pages?.[0]?.page || "—"} sub={`${data.top_pages?.[0]?.views || 0} zobrazení`} color="purple" />
        <StatCard icon={LogOut} label="Top exit" value={data.top_exits?.[0]?.page || "—"} sub={`${data.top_exits?.[0]?.exits || 0} odchodov`} color="red" />
      </div>

      {/* Upsell funnel — conversion from /results CTA */}
      {data.upsell_funnel && data.upsell_funnel.loaded_unique > 0 && (
        <div data-testid="admin-upsell-funnel">
          <h3 className="text-sm font-bold text-gray-500 uppercase mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-500" /> Upsell funnel — /results CTA
          </h3>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <UpsellStat
                label="Videli ponuku"
                value={data.upsell_funnel.loaded_unique}
                sub="unique sessions"
                tone="neutral"
              />
              <UpsellStat
                label='Klik na "Zvýšte skóre" (€)'
                value={data.upsell_funnel.upsell_click_unique}
                sub={`${data.upsell_funnel.upsell_conversion_pct}% conversion`}
                tone="yellow"
                testid="admin-upsell-yellow-clicks"
              />
              <UpsellStat
                label='Klik na "Podrobnosti"'
                value={data.upsell_funnel.details_click_unique}
                sub={`${data.upsell_funnel.details_click_pct}% conversion`}
                tone="muted"
              />
            </div>
            {/* Comparison bar */}
            <div className="flex h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="bg-yellow-400"
                style={{ width: `${data.upsell_funnel.upsell_conversion_pct}%` }}
                title={`${data.upsell_funnel.upsell_conversion_pct}% → pricing`}
              />
              <div
                className="bg-gray-400"
                style={{ width: `${data.upsell_funnel.details_click_pct}%` }}
                title={`${data.upsell_funnel.details_click_pct}% → details`}
              />
            </div>
            <p className="text-[10px] text-gray-500 font-mono mt-2">
              Žltá = revenue path (pricing), sivá = info path (share-gate). Zvyšok = bounce.
            </p>

            {/* Per-tier breakdown — score-based CTA conversion */}
            {Array.isArray(data.upsell_funnel.per_tier) &&
              data.upsell_funnel.per_tier.some(t => t.loaded > 0) && (
              <div className="mt-6 pt-5 border-t border-gray-100" data-testid="admin-upsell-funnel-tiers">
                <p className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-3">
                  Per-tier breakdown — score-based CTA
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
                      <th className="text-left py-2">Tier</th>
                      <th className="text-right py-2">Loaded</th>
                      <th className="text-right py-2">Upsell €</th>
                      <th className="text-right py-2">Details</th>
                      <th className="text-right py-2">Conv %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.upsell_funnel.per_tier.map(t => {
                      const tierStyle = {
                        low:  { bg: "bg-red-100",     dot: "bg-red-500",     label: "Low (<40)"  },
                        mid:  { bg: "bg-yellow-100",  dot: "bg-yellow-400",  label: "Mid (40-69)" },
                        high: { bg: "bg-emerald-100", dot: "bg-emerald-500", label: "High (70+)" },
                      }[t.tier] || { bg: "", dot: "bg-gray-300", label: t.tier };
                      return (
                        <tr key={t.tier} className={`border-t border-gray-100 ${tierStyle.bg}/50`}
                          data-testid={`admin-upsell-tier-${t.tier}`}>
                          <td className="py-2 flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${tierStyle.dot}`} />
                            <span className="font-bold">{tierStyle.label}</span>
                          </td>
                          <td className="text-right py-2 font-mono">{t.loaded}</td>
                          <td className="text-right py-2 font-mono font-bold">{t.upsell_clicks}</td>
                          <td className="text-right py-2 font-mono text-gray-500">{t.details_clicks}</td>
                          <td className="text-right py-2 font-mono font-black">
                            <span className={t.upsell_conversion_pct >= 15 ? "text-emerald-600" : t.upsell_conversion_pct >= 5 ? "text-yellow-700" : "text-gray-500"}>
                              {t.upsell_conversion_pct}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-[10px] text-gray-400 font-mono mt-3 italic">
                  Hypotéza: low-score užívatelia konvertujú 4× lepšie (vyššia bolesť → vyššia ochota platiť).
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Funnel */}
      {funnelData.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-500 uppercase mb-4">Konverzný funnel</h3>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="space-y-3">
              {funnelData.map((f, i) => {
                const pct = Math.round((f.users / maxFunnel) * 100);
                const dropoff = i > 0 ? Math.round(((funnelData[i-1].users - f.users) / funnelData[i-1].users) * 100) : 0;
                return (
                  <div key={f.page}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{f.page === "/" ? "Homepage" : f.page}</span>
                      <div className="flex items-center gap-3">
                        {dropoff > 0 && <span className="text-xs text-red-500 font-mono">-{dropoff}% drop</span>}
                        <span className="text-sm font-bold font-mono">{f.users}</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-6">
                      <div className="h-6 rounded-full flex items-center pl-3 text-xs text-white font-bold transition-all" style={{ width: `${Math.max(pct, 8)}%`, background: `hsl(${210 - i * 30}, 70%, ${50 + i * 5}%)` }}>
                        {pct}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div>
          <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Top stránky (zobrazenia)</h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden text-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700"><tr><th className="text-left px-4 py-2 font-semibold">Stránka</th><th className="text-right px-4 py-2 font-semibold">Zobrazenia</th><th className="text-right px-4 py-2 font-semibold">Unikátne</th></tr></thead>
              <tbody>
                {(data.top_pages || []).slice(0, 10).map((p, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-mono text-xs text-gray-800">{p.page}</td>
                    <td className="text-right px-4 py-2 font-bold text-gray-900">{p.views}</td>
                    <td className="text-right px-4 py-2 text-gray-500">{p.unique}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Exit Pages */}
        <div>
          <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Exit stránky (kde odchádzajú)</h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden text-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700"><tr><th className="text-left px-4 py-2 font-semibold">Stránka</th><th className="text-right px-4 py-2 font-semibold">Odchody</th></tr></thead>
              <tbody>
                {(data.top_exits || []).map((p, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-mono text-xs text-gray-800">{p.page}</td>
                    <td className="text-right px-4 py-2 font-bold text-red-600">{p.exits}</td>
                  </tr>
                ))}
                {(data.top_exits || []).length === 0 && <tr><td colSpan={2} className="px-4 py-6 text-center text-gray-400">Zatiaľ žiadne dáta</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Click Heatmap */}
        <div>
          <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Top kliky (na čo klikajú)</h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700"><tr><th className="text-left px-4 py-2">Element</th><th className="text-right px-4 py-2">Kliky</th></tr></thead>
              <tbody>
                {(data.top_clicks || []).slice(0, 10).map((c, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-mono text-xs truncate max-w-[200px]">{c.target || "—"}</td>
                    <td className="text-right px-4 py-2 font-bold text-blue-600">{c.clicks}</td>
                  </tr>
                ))}
                {(data.top_clicks || []).length === 0 && <tr><td colSpan={2} className="px-4 py-6 text-center text-gray-400">Zatiaľ žiadne dáta</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Time on Page */}
        <div>
          <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Čas na stránke (priemerný)</h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700"><tr><th className="text-left px-4 py-2">Stránka</th><th className="text-right px-4 py-2">Priem. čas</th></tr></thead>
              <tbody>
                {(data.time_on_page || []).map((p, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-mono text-xs">{p.page}</td>
                    <td className="text-right px-4 py-2 font-bold">{p.avg_time}s</td>
                  </tr>
                ))}
                {(data.time_on_page || []).length === 0 && <tr><td colSpan={2} className="px-4 py-6 text-center text-gray-400">Zatiaľ žiadne dáta</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Scroll Depth */}
      <div>
        <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Scroll depth (koľko % videli)</h3>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          {(data.scroll_depth || []).length === 0 ? (
            <p className="text-center text-gray-400 py-4">Zatiaľ žiadne dáta</p>
          ) : (
            <div className="space-y-2">
              {(data.scroll_depth || []).map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-mono text-gray-500 w-32 truncate">{s.page}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4">
                    <div className="h-4 bg-blue-500 rounded-full flex items-center justify-end pr-2 text-[10px] text-white font-bold" style={{ width: `${Math.max(s.avg_depth, 5)}%` }}>
                      {s.avg_depth}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Sessions */}
      <div>
        <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Posledné sessions</h3>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700"><tr><th className="text-left px-4 py-2">Session</th><th className="text-right px-4 py-2">Eventy</th><th className="text-right px-4 py-2">Stránky</th><th className="text-left px-4 py-2">Posledná stránka</th><th className="text-right px-4 py-2">Čas</th></tr></thead>
            <tbody>
              {(data.recent_sessions || []).map((s, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{s.session_id?.slice(0, 16)}...</td>
                  <td className="text-right px-4 py-2 font-bold">{s.events}</td>
                  <td className="text-right px-4 py-2">{s.pages_count}</td>
                  <td className="px-4 py-2 font-mono text-xs">{s.last_page}</td>
                  <td className="text-right px-4 py-2 text-xs text-gray-500">{s.last_ts ? new Date(s.last_ts).toLocaleString("sk") : "—"}</td>
                </tr>
              ))}
              {(data.recent_sessions || []).length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Zatiaľ žiadne dáta</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "blue" }) {
  const colors = { blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600", purple: "bg-purple-50 text-purple-600", red: "bg-red-50 text-red-600" };
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`stat-${label}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color]} mb-2`}><Icon className="w-4 h-4" /></div>
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className="text-lg font-bold font-mono mt-0.5">{typeof value === "number" ? value.toLocaleString() : value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}


function UpsellStat({ label, value, sub, tone = "neutral", testid }) {
  const palette = {
    yellow: "bg-yellow-50 border-yellow-300 text-yellow-900",
    muted:  "bg-gray-50 border-gray-200 text-gray-700",
    neutral:"bg-white border-gray-200 text-gray-800",
  }[tone] || "bg-white border-gray-200 text-gray-800";
  return (
    <div className={`p-4 border rounded-lg ${palette}`} data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-3xl font-black mt-1">{value}</p>
      {sub && <p className="text-xs opacity-70 mt-1 font-mono">{sub}</p>}
    </div>
  );
}
